import moment from 'moment';
import 'moment-timezone';
import {
  NewspaperOrderPricing,
  NewspaperOrderRefundItem,
  NewspaperOrderStatus,
  NewspaperOrder
} from '../types/newspaperOrder';
import { NewspaperOrderModel } from '../model/objects/newspaperOrderModel';
import {
  ResponseOrError,
  getErrors,
  getResponses,
  wrapError,
  wrapSuccess
} from '../types/responses';
import { EFirebaseContext, ERef } from '../types';
import {
  Order,
  isAdvertiserWithOrganizationOrder,
  isIndividualAdvertiserOrder
} from '../types/order';
import { filterAsync, mapAsync } from '../helpers/array/async';
import { getErrorReporter } from '../utils/errors';
import { dateTimeLikeToDate } from '../date';
import { ColumnService } from './directory';
import { removeUndefinedFields } from '../helpers';
import { BillingTermType, Product } from '../enums';
import CustomerService from './customerService';
import CustomerOrganizationService from './customerOrganizationService';
import { OrganizationModel } from '../model/objects/organizationModel';
import { safeGetModelFromRef } from '../model/getModel';
import { BadRequestError } from '../errors/ColumnErrors';
import { getAmountWithCloseoutVariance } from '../payments/refunds';

export class NewspaperOrderService {
  constructor(private context: EFirebaseContext) {}

  /**
   * Computes the determinative billing term for a newspaper order using available customer/customerOrganization overrides,
   * falling back to product defaults and newspaper configuration. Never returns the default billing term.
   * Validates required fields on the provided partial NewspaperOrder and the associated order.
   */
  public async getOperativeBillingTerm(
    orderData: Order,
    newspaperOrder: Partial<NewspaperOrder>
  ): Promise<ResponseOrError<number>> {
    if (!newspaperOrder || !newspaperOrder.newspaper) {
      return wrapError(
        new BadRequestError(
          'Missing newspaper on newspaperOrder when computing billing term'
        )
      );
    }

    // 1. Customer-level override (non-default) takes precedence
    if (isIndividualAdvertiserOrder(orderData)) {
      const [custErr, customer] = await new CustomerService(
        this.context
      ).maybeGetCustomerFromUserAndOrg(
        orderData.advertiser,
        newspaperOrder.newspaper
      );
      if (custErr) {
        return wrapError(custErr);
      }
      if (
        customer &&
        customer.data().billingTerm &&
        customer.data().billingTerm !== BillingTermType.default.value
      ) {
        return wrapSuccess(customer.data().billingTerm);
      }
    } else if (isAdvertiserWithOrganizationOrder(orderData)) {
      // Try customer first if present
      const [custErr, customer] = await new CustomerService(
        this.context
      ).maybeGetCustomerFromUserAndOrg(
        orderData.advertiser,
        newspaperOrder.newspaper
      );
      if (custErr) {
        return wrapError(custErr);
      }
      if (
        customer &&
        customer.data().billingTerm &&
        customer.data().billingTerm !== BillingTermType.default.value
      ) {
        return wrapSuccess(customer.data().billingTerm);
      }
      // 2. CustomerOrganization-level override (non-default)
      const [custOrgErr, custOrg] = await new CustomerOrganizationService(
        this.context
      ).maybeGetCustomerOrgFromAdvertiserOrgAndNewspaper(
        orderData.advertiserOrganization,
        newspaperOrder.newspaper
      );
      if (custOrgErr) {
        return wrapError(custOrgErr);
      }
      if (
        custOrg &&
        custOrg.data().billingTerm &&
        custOrg.data().billingTerm !== BillingTermType.default.value
      ) {
        return wrapSuccess(custOrg.data().billingTerm);
      }
    }

    // 3a. Product default: obituary/classified require upfront payment
    const { product } = orderData;
    if ([Product.Obituary, Product.Classified].includes(product)) {
      return wrapSuccess(BillingTermType.upfront_payment.value);
    }

    // 3b. Notices: follow newspaper's requireUpfrontPayment flag; else net 30
    const [newsErr, newspaper] = await safeGetModelFromRef(
      OrganizationModel,
      this.context,
      newspaperOrder.newspaper
    );
    if (newsErr) {
      return wrapError(newsErr);
    }
    const billingTerm = newspaper.modelData.requireUpfrontPayment
      ? BillingTermType.upfront_payment.value
      : BillingTermType.net_thirty.value;
    return wrapSuccess(billingTerm);
  }

  public async getNewspaperOrderWithEarliestDeadline(
    newspaperOrders: NewspaperOrderModel[]
  ): Promise<
    ResponseOrError<{
      newspaperOrder: NewspaperOrderModel;
      deadline: moment.Moment;
    }>
  > {
    if (!newspaperOrders.length) {
      return wrapError(new Error('No newspaper orders provided'));
    }

    const newspaperOrderAndDeadlineResults = await Promise.all(
      newspaperOrders.map(async no => {
        const { response: deadline, error: deadlineError } =
          await no.getDeadline();
        if (deadlineError) {
          return wrapError(deadlineError);
        }

        return wrapSuccess({
          newspaperOrder: no,
          deadline
        });
      })
    );
    const newspaperOrderAndDeadlineResultErrors = getErrors(
      newspaperOrderAndDeadlineResults
    );
    if (newspaperOrderAndDeadlineResultErrors.length) {
      return wrapError(newspaperOrderAndDeadlineResultErrors[0]);
    }
    const newspaperOrdersAndDeadlines = getResponses(
      newspaperOrderAndDeadlineResults
    );

    const sortedNewspaperOrdersAndDeadlines = newspaperOrdersAndDeadlines.sort(
      (a, b) => a.deadline.valueOf() - b.deadline.valueOf()
    );

    return wrapSuccess(sortedNewspaperOrdersAndDeadlines[0]);
  }

  public canCancelNewspaperOrders = async (
    newspaperOrders: NewspaperOrderModel[],
    isPublisher: boolean
  ): Promise<ResponseOrError<boolean>> => {
    const newspaperIds = new Set(
      newspaperOrders.map(no => no.modelData.newspaper.id)
    );
    if (isPublisher && newspaperIds.size === 1) {
      return wrapSuccess(true);
    }

    const [pastDeadlineError, someNewspaperOrderIsPastDeadline] =
      await this.isAnyNewspaperOrderPastDeadline(newspaperOrders);
    if (pastDeadlineError) {
      return wrapError(pastDeadlineError);
    }
    if (someNewspaperOrderIsPastDeadline) {
      getErrorReporter().logInfo(
        `Order ${newspaperOrders[0].ref.parent.parent?.id} has a newspaperOrder that has passed the publishing deadline`
      );
      return wrapSuccess(false);
    }
    return wrapSuccess(true);
  };

  public async isAnyNewspaperOrderPastDeadline(
    newspaperOrders: NewspaperOrderModel[]
  ): Promise<ResponseOrError<boolean>> {
    const {
      response: earliestDeadlineAndNewspaperOrder,
      error: earliestDeadlineError
    } = await this.getNewspaperOrderWithEarliestDeadline(newspaperOrders);
    if (earliestDeadlineError) {
      return wrapError(earliestDeadlineError);
    }

    return wrapSuccess(
      moment().isAfter(earliestDeadlineAndNewspaperOrder.deadline)
    );
  }

  public async getNewspaperOrdersAtLeastNHoursBeforeDeadline(
    newspaperOrders: NewspaperOrderModel[],
    hoursBeforeDeadline: number
  ): Promise<ResponseOrError<NewspaperOrderModel[]>> {
    const result = await filterAsync(newspaperOrders, async no => {
      const {
        response: isNHoursBeforeDeadline,
        error: isNHoursBeforeDeadlineError
      } = await no.isAtLeastNHoursBeforeDeadline(hoursBeforeDeadline);
      if (isNHoursBeforeDeadlineError) {
        return wrapError(isNHoursBeforeDeadlineError);
      }

      return wrapSuccess(isNHoursBeforeDeadline ? no : null);
    });

    return result;
  }

  public async cloneForEditOrDuplicateFlow(
    orderRef: ERef<Order>,
    antecedentNewspaperOrders: NewspaperOrderModel[],
    newVersion: number
  ): Promise<void> {
    await Promise.all(
      antecedentNewspaperOrders.map(async currentNewspaperOrder => {
        const src = currentNewspaperOrder.modelData;
        const newNewspaperOrder: NewspaperOrder = {
          orderVersion: newVersion,
          newspaper: src.newspaper,
          publishingMedium: src.publishingMedium,
          filingType: src.filingType,
          layout: src.layout,
          publishingDates: src.publishingDates,
          status: NewspaperOrderStatus.DRAFT,
          colorEnablement: src.colorEnablement,
          jobcaseEnablement: src.jobcaseEnablement,
          oldFilingType: src.oldFilingType,
          package: src.package ?? null,
          adTemplate: src.adTemplate,
          modularSize: src.modularSize,
          colorOptions: src.colorOptions,
          jobcaseSettings: src.jobcaseSettings,
          displayParams: src.displayParams
        };

        const ref = await this.context
          .orderNewspaperOrdersRef(orderRef)
          .add(removeUndefinedFields(newNewspaperOrder));
        getErrorReporter().logInfo(
          'Created new newspaper order for edit/duplicate flow',
          {
            antecedentNewspaperOrderId: currentNewspaperOrder.id,
            newNewspaperOrderId: ref.id,
            newVersion: `${newVersion}`
          }
        );
      })
    );
  }

  /**
   * Proportionally calculates the total refundable amount from a line item amount to include
   * portions of tax and convenience fees.
   */
  private getRefundableAmountFromLineItemAmount(
    newspaperOrderPricing: NewspaperOrderPricing,
    lineItemAmount: number
  ): number {
    // Guard against malformed pricing data producing NaN refund amounts
    const { subtotalInCents, totalInCents } = newspaperOrderPricing;
    // Validate base numeric inputs
    if (
      !Number.isFinite(lineItemAmount) ||
      !Number.isFinite(subtotalInCents) ||
      !Number.isFinite(totalInCents) ||
      subtotalInCents <= 0 ||
      totalInCents < 0
    ) {
      // Fallback: if we cannot proportionally allocate fees/taxes, only refund the raw line item amount.
      // This prevents NaN from propagating into higher-level refund logic.
      getErrorReporter().logWarn(
        'Refundable amount fallback applied due to $0 subtotal or unexpected pricing values',
        {
          subtotalInCents: String(subtotalInCents),
          totalInCents: String(totalInCents),
          lineItemAmount: String(lineItemAmount)
        }
      );
      return Math.max(
        0,
        Math.round(Number.isFinite(lineItemAmount) ? lineItemAmount : 0)
      );
    }
    const additionalCharges = totalInCents - subtotalInCents;
    const proportionOfAdditionalChargesForLineItem =
      lineItemAmount / subtotalInCents;
    const refundableAmount =
      lineItemAmount +
      Math.round(proportionOfAdditionalChargesForLineItem * additionalCharges);
    return Math.max(0, refundableAmount);
  }

  /**
   * Builds refund items for newspaper orders with optional moment flag to indicate
   * date after which line items are refundable.
   * For each newspaper order we sum the refundable future line item amounts (in cents)
   * and clamp by the remaining refundable amount (accounting for prior + pending refunds).
   * Returns only newspaperOrders with a positive refundable amount remaining.
   */
  public async buildNewspaperOrderRefundItems(
    newspaperOrders: NewspaperOrderModel[],
    refundPublishingDatesAfter: moment.Moment | undefined
  ): Promise<ResponseOrError<NewspaperOrderRefundItem[]>> {
    const [refundItemsError, refundItems] = await mapAsync(
      newspaperOrders,
      async newspaperOrder => {
        const { pricing } = newspaperOrder.modelData;
        if (!pricing || !pricing.lineItems || pricing.lineItems.length === 0) {
          getErrorReporter().logWarn(
            'No pricing or line items found for newspaper order',
            {
              newspaperOrderId: newspaperOrder.id
            }
          );
          return wrapSuccess(null);
        }
        const [newspaperSnapError, newspaperSnap] =
          await newspaperOrder.getNewspaper();
        if (newspaperSnapError) {
          return wrapError(newspaperSnapError);
        }
        const tz = newspaperSnap.modelData.iana_timezone || 'UTC';
        let refundableAmount = 0;
        for (const li of pricing.lineItems) {
          const liDate = dateTimeLikeToDate(li.date);
          const liDateStartOfDay = moment.tz(liDate, tz).startOf('day');
          if (
            refundPublishingDatesAfter === undefined ||
            liDateStartOfDay.isAfter(refundPublishingDatesAfter)
          ) {
            refundableAmount += this.getRefundableAmountFromLineItemAmount(
              pricing,
              li.amount
            );
          }
        }
        if (!Number.isFinite(refundableAmount)) {
          const error = new Error(
            'Calculated refundable amount is NaN or Infinite for newspaper order'
          );
          getErrorReporter().logAndCaptureCriticalError(
            ColumnService.PAYMENTS,
            error,
            'Failed to build cancellation refund item due to invalid refundable amount',
            {
              newspaperOrderId: newspaperOrder.id
            }
          );
          return wrapError(error);
        }
        if (refundableAmount <= 0) {
          return wrapSuccess(null);
        }

        // Account for prior and pending refunds
        const alreadyRefunded = pricing.totalRefundedInCents || 0;
        if ((pricing.pendingRefundInCents || 0) > 0) {
          const error = new Error(
            'Pending refund exists on newspaper order when building cancellation refund items'
          );
          getErrorReporter().logAndCaptureCriticalError(
            ColumnService.PAYMENTS,
            error,
            'Failed to build cancellation refund item',
            {
              newspaperOrderId: newspaperOrder.id
            }
          );
          return wrapError(error);
        }
        const amountInCents = getAmountWithCloseoutVariance({
          totalAmount: pricing.totalInCents,
          alreadyProcessed: alreadyRefunded,
          requestedAmount: refundableAmount
        });
        if (amountInCents === 0) {
          getErrorReporter().logWarn(
            `No refundable amount for newspaper order ${newspaperOrder.id} due to prior or pending refunds`
          );
          return wrapSuccess(null);
        }
        return {
          newspaperOrderId: newspaperOrder.id,
          amountInCents
        };
      }
    );
    if (refundItemsError) {
      return wrapError(refundItemsError);
    }
    return wrapSuccess(
      refundItems.filter((item): item is NewspaperOrderRefundItem => !!item)
    );
  }
}
